import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPendingProducts,
  fetchRejectedProducts,
  fetchProductStats,
  approveProduct,
  fetchAllProducts,
  fetchApprovedProducts,
  fetchCategories,
  updateProductCategory,
} from "../../apis/products";
import { toast } from "react-toastify";
import { DateRangeType } from "../../pages/ui/DateRangeSelector";

interface ProductState {
  loading: boolean;
  error: string | null;
  allProducts: any;
  approvedProducts: any;
  pendingProducts: any;
  rejectedProducts: any;
  productStats: any;
  productDetails: any;
  approvalResponse: any;
  categories: any;

  // filter
  productPage: number;
  productPageLimit: number;
  status: "ALL" | "PENDING" | "APPROVED" | "REJECTED";
  search: string | null;
  dateRange: DateRangeType;
  categoryId: string | null;
  vendorId: string | null;
}

const initialState: ProductState = {
  loading: false,
  error: null,
  pendingProducts: null,
  rejectedProducts: null,
  allProducts: null,
  approvedProducts: null,
  productStats: null,
  productDetails: null,
  approvalResponse: null,
  categories: null,

  // filter
  productPage: 0,
  productPageLimit: 20,
  status: "ALL",
  search: null,
  dateRange: {
    startDate: null,
    endDate: null,
  },
  categoryId: null,
  vendorId: null,
};

// Fetch stats
export const fetchCategoriesAsync = createAsyncThunk(
  "Product/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetchCategories();
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ?? "Failed to fetch product categories"
      );
    }
  }
);
export const fetchProductStatsAsync = createAsyncThunk(
  "Product/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetchProductStats();
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ?? "Failed to fetch product stats"
      );
    }
  }
);

// Fetch all
export const fetchAllProductsAsync = createAsyncThunk(
  "Product/fetchAll",
  async (params: any, { rejectWithValue }) => {
    try {
      const res = await fetchAllProducts(params);
      return res?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ?? "Failed to fetch all products"
      );
    }
  }
);

// Fetch approved
export const fetchApprovedProductsAsync = createAsyncThunk(
  "Product/fetchApproved",
  async (params: any, { rejectWithValue }) => {
    try {
      const res = await fetchApprovedProducts(params);
      return res?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ?? "Failed to fetch approved products"
      );
    }
  }
);
// Fetch approved
export const fetchPendingProductsAsync = createAsyncThunk(
  "Product/fetchPending",
  async (params: any, { rejectWithValue }) => {
    try {
      const res = await fetchPendingProducts(params);
      return res?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ?? "Failed to fetch pending products"
      );
    }
  }
);

// Fetch rejected
export const fetchRejectedProductsAsync = createAsyncThunk(
  "Product/fetchRejected",
  async (params: any, { rejectWithValue }) => {
    try {
      const res = await fetchRejectedProducts(params);
      return res?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ?? "Failed to fetch rejected products"
      );
    }
  }
);

// Approve product
export const approveProductAsync = createAsyncThunk(
  "Product/approveProduct",
  async (
    payload: {
      productId: string;
      approvalStatus: string;
      rejectionReason?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await approveProduct(payload);
      return res;
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to approve product");
      return rejectWithValue(
        err.response?.data?.message ?? "Failed to approve product"
      );
    }
  }
);
// Approve product
export const updateProductCategoryAsync = createAsyncThunk(
  "Product/updateProductCategory",
  async (
    payload: {
      id: string;
      categoryIds: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await updateProductCategory(payload);
      return res;
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ?? "Failed to update product category"
      );
      return rejectWithValue(
        err.response?.data?.message ?? "Failed to update product category"
      );
    }
  }
);

const productSlice = createSlice({
  name: "Product",
  initialState,
  reducers: {
    clearApprovalResponse: (state) => {
      state.approvalResponse = null;
    },
    setProductPage: (state, action) => {
      state.productPage = action.payload;
    },
    setProductPageLimit: (state, action) => {
      state.productPageLimit = action.payload;
    },
    updateFilters: (
      state,
      action: PayloadAction<
        Partial<{
          productPage: number;
          productPageLimit: number;
          status: string | null;
          search: string | null;
          dateRange: DateRangeType;
          categoryId: string | null;
          vendorId: string | null;
        }>
      >
    ) => {
      Object.assign(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Stats
      .addCase(fetchProductStatsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductStatsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.productStats = action.payload;
      })
      .addCase(
        fetchProductStatsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // all products
      .addCase(fetchAllProductsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllProductsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.allProducts = action.payload;
      })
      .addCase(
        fetchAllProductsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Approved products
      .addCase(fetchApprovedProductsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchApprovedProductsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.approvedProducts = action.payload;
      })
      .addCase(
        fetchApprovedProductsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Pending products
      .addCase(fetchPendingProductsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingProductsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingProducts = action.payload;
      })
      .addCase(
        fetchPendingProductsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Rejected products
      .addCase(fetchRejectedProductsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRejectedProductsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.rejectedProducts = action.payload;
      })
      .addCase(
        fetchRejectedProductsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Approve product
      .addCase(approveProductAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveProductAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.approvalResponse = action.payload;
      })
      .addCase(
        approveProductAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )
      // update product category
      .addCase(updateProductCategoryAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProductCategoryAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(
        updateProductCategoryAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )
      // product categories
      .addCase(fetchCategoriesAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategoriesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(
        fetchCategoriesAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export const {
  clearApprovalResponse,
  setProductPage,
  setProductPageLimit,
  updateFilters,
} = productSlice.actions;
export default productSlice.reducer;
